import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { formatRelative, type DateFormatOptions } from "@/lib/format";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Intl formatters are expensive to construct — build each once at module
// scope (per-currency instances are cached below) instead of per call.
const NUMBER_FORMAT = new Intl.NumberFormat();
const RATE_FORMAT = new Intl.NumberFormat("da-DK");
const CURRENCY_FORMATS = new Map<string, Intl.NumberFormat>();

const currencyFormat = (currency: string): Intl.NumberFormat => {
  let format = CURRENCY_FORMATS.get(currency);
  if (!format) {
    format = new Intl.NumberFormat("da-DK", {
      style: "currency",
      currency,
      currencyDisplay: "code",
    });
    CURRENCY_FORMATS.set(currency, format);
  }
  return format;
};

export function formatNumber(number: number): string {
  return NUMBER_FORMAT.format(number);
}

/**
 * Formats a monetary amount in an explicit currency.
 *
 * `currency` is REQUIRED on purpose — an assignment's currency is the single
 * source of truth for the money it involves, and defaulting it would silently
 * misstate commercial terms (e.g. rendering 1200 SEK as 1200 DKK). Making it
 * required turns a missing currency into a build-time TypeScript error.
 *
 * The ISO code is always shown (`currencyDisplay: "code"`) rather than the
 * locale symbol, because the `da-DK` symbol for DKK is "kr." — indistinguishable
 * at a glance from the Swedish and Norwegian krone. The `da-DK` locale is kept
 * for number grouping and decimals (1.200,00), matching the rest of the portal.
 */
export function formatCurrency(amount: number, currency: string): string {
  return currencyFormat(currency).format(amount);
}

/**
 * Formats a money amount the backend serialises as a Decimal **string**
 * (e.g. invoice / payment amounts like "1234.56"). Parses to a number for
 * display only — precision-sensitive arithmetic stays on the backend. A
 * null/blank/unparseable value renders as a zero amount so the UI never shows
 * "NaN". Currency stays required, per the {@link formatCurrency} rule (#614).
 */
export function formatMoney(amount: string | number | null | undefined, currency: string): string {
  const n = typeof amount === "number" ? amount : Number(amount);
  return formatCurrency(Number.isFinite(n) ? n : 0, currency);
}

/**
 * Formats an amount as digits only, with no currency unit.
 *
 * Consultant profile and application rates carry an optional currency since
 * backend#883 (`hourly_rate_currency`); rates entered before that have none,
 * and rendering a unit for them would be inventing one. Use
 * `formatProfileRate` from `@/lib/rateCurrency` for profile rates — it appends
 * the code when known and falls back to this. The currency of real work is
 * agreed on the assignment; use {@link formatCurrency} there.
 */
export function formatRateWithoutCurrency(amount: number): string {
  return RATE_FORMAT.format(amount);
}

/** Explanatory hint shown next to a rate whose currency is unknown (pre-#883). */
export const NO_CURRENCY_HINT =
  "Currency not recorded for this rate — the currency of actual work is agreed per assignment";

export function timeAgo(date: Date, options?: DateFormatOptions): string {
  return formatRelative(date, options);
}

export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 17) {
    return "Good afternoon";
  }
  return "Good evening";
}
